import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Search, Package, Truck, Clock, CheckCircle2, XCircle, RefreshCw, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';
import AdminLayout from '@/components/AdminLayout';
import AddressSearch from '@/components/AddressSearch';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

const STATUS_TABS = [
  { key: '',                 label: '전체' },
  { key: 'payment_pending',  label: '결제대기' },
  { key: 'paid',             label: '결제완료' },
  { key: 'preparing',        label: '배송준비' },
  { key: 'shipping',         label: '배송중' },
  { key: 'delivered',        label: '배송완료' },
  { key: 'cancelled',        label: '취소' },
  { key: 'refunded',         label: '환불' },
];

const STATUS_STYLE = {
  payment_pending: 'text-amber-700 bg-amber-100 dark:bg-amber-500/20',
  paid:            'text-blue-700 bg-blue-100 dark:bg-blue-500/20',
  preparing:       'text-indigo-700 bg-indigo-100 dark:bg-indigo-500/20',
  shipping:        'text-violet-700 bg-violet-100 dark:bg-violet-500/20',
  delivered:       'text-emerald-700 bg-emerald-100 dark:bg-emerald-500/20',
  confirmed:       'text-emerald-700 bg-emerald-100 dark:bg-emerald-500/20',
  cancelled:       'text-gray-600 bg-gray-100 dark:bg-white/10',
  refund_requested:'text-orange-700 bg-orange-100 dark:bg-orange-500/20',
  refunded:        'text-gray-600 bg-gray-100 dark:bg-white/10',
};

const ACTION_LABELS = {
  'status.update': '상태 변경',
  'status.rollback':'이전 단계로 되돌리기',
  'invoice.set':   '송장 등록',
  'address.update':'배송정보 수정',
  'cancel':        '취소',
  'refund':        '환불',
};

const AdminOrders = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({});
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editAddr, setEditAddr] = useState(false);
  const [addrForm, setAddrForm] = useState({});
  const detailInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (q) params.q = q;
      const [listRes, sumRes] = await Promise.all([
        api.get('/admin/orders', { params }),
        api.get('/admin/orders/summary'),
      ]);
      setOrders(listRes.data || []);
      setSummary(sumRes.data?.by_status || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || '주문 목록을 불러오지 못했습니다.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status, q]); // eslint-disable-line

  const openDetail = async (orderId) => {
    setOpenId(orderId);
    setDetail(null);
    setHistory([]);
    setEditAddr(false);
    try {
      const [dRes, hRes] = await Promise.all([
        api.get(`/admin/orders/${orderId}`),
        api.get(`/admin/orders/${orderId}/history`).catch(() => ({ data: [] })),
      ]);
      setDetail(dRes.data);
      setHistory(hRes.data || []);
    } catch (e) {
      toast.error('상세 조회 실패');
      setOpenId(null);
    }
  };
  const closeDetail = () => { setOpenId(null); setDetail(null); setHistory([]); setEditAddr(false); };

  const applySearchResult = (r) => setAddrForm((f) => ({
    ...f,
    postal_code: r.postal_code, road_address: r.road_address, jibun_address: r.jibun_address,
    base_address: r.base_address, extra_address: r.extra_address,
    address1: (r.base_address + (r.extra_address ? ' ' + r.extra_address : '')).trim(),
  }));

  const startEditAddr = () => {
    const a = detail?.shipping_address_snapshot || {};
    setAddrForm({ ...a, buyer_message: detail?.buyer_message || '' });
    setEditAddr(true);
  };
  const saveEditAddr = async () => {
    setBusy(true);
    try {
      const res = await api.patch(`/admin/orders/${openId}/shipping-address`, {
        ...addrForm,
        expected_updated_at: detail?.updated_at,
      });
      setDetail(res.data);
      const h = await api.get(`/admin/orders/${openId}/history`).catch(() => ({ data: [] }));
      setHistory(h.data || []);
      setEditAddr(false);
      toast.success('배송정보가 수정되었습니다.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '수정 실패'); }
    finally { setBusy(false); }
  };

  const changeStatus = async (newStatus) => {
    if (!openId) return;
    if (!window.confirm(`주문 상태를 "${newStatus}"로 변경하시겠습니까?`)) return;
    setBusy(true);
    try {
      const res = await api.patch(`/admin/orders/${openId}/status`, { status: newStatus });
      setDetail(res.data);
      toast.success('상태가 변경되었습니다.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '상태 변경 실패');
    } finally { setBusy(false); }
  };

  const cancelOrder = async () => {
    if (!openId) return;
    const reason = window.prompt('취소 사유를 입력해주세요.', '관리자 취소');
    if (reason == null) return;
    setBusy(true);
    try {
      const res = await api.post(`/admin/orders/${openId}/cancel`, { reason });
      setDetail(res.data);
      toast.success('주문이 취소되었습니다.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '취소 실패');
    } finally { setBusy(false); }
  };

  const refundOrder = async () => {
    if (!openId) return;
    const reason = window.prompt('환불 사유를 입력해주세요.', '관리자 환불 승인');
    if (reason == null) return;
    if (!window.confirm('환불을 승인하시겠습니까? (실제 PG 환불 API는 다음 단계에서 연동됩니다)')) return;
    setBusy(true);
    try {
      const res = await api.post(`/admin/orders/${openId}/refund`, { reason });
      setDetail(res.data);
      toast.success('환불이 처리되었습니다.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '환불 실패');
    } finally { setBusy(false); }
  };

  return (
    <AdminLayout user={user} active="orders">
      <div className="mb-6 flex items-center gap-3">
        <ShoppingCart className="text-[#FF3B30]" size={22} />
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="admin-orders-title">주문 관리</h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-orders-tabs">
        {STATUS_TABS.map((t) => (
          <button key={t.key || 'all'} type="button" onClick={() => setStatus(t.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${status === t.key ? 'bg-[#FF3B30] border-[#FF3B30] text-white' : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300'}`}
            data-testid={`admin-orders-tab-${t.key || 'all'}`}>
            {t.label}
            {t.key && summary[t.key] != null && <span className="ml-1 text-[10px] opacity-70">({summary[t.key]})</span>}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={qInput} onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setQ(qInput); }}
            placeholder="주문번호 · 구매자명 · 이메일 검색"
            className="pl-8 pr-3 py-2 w-full text-sm rounded-full border border-black/10 dark:border-white/10 bg-transparent"
            data-testid="admin-orders-search" />
        </div>
        <button type="button" onClick={() => setQ(qInput)} className="px-3 py-2 rounded-full bg-black text-white text-xs font-bold" data-testid="admin-orders-search-btn">검색</button>
        <button type="button" onClick={load} className="px-3 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold inline-flex items-center gap-1" data-testid="admin-orders-refresh">
          <RefreshCw size={12} /> 새로고침
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 py-6">불러오는 중…</div>
      ) : orders.length === 0 ? (
        <div className="py-16 text-center bg-black/[0.02] dark:bg-white/[0.03] rounded-2xl border border-dashed border-black/10 dark:border-white/10" data-testid="admin-orders-empty">
          <Package className="mx-auto text-gray-300" size={28} />
          <p className="mt-3 text-sm font-bold">주문이 없습니다.</p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="min-w-full text-sm" data-testid="admin-orders-table">
            <thead className="bg-black/5 dark:bg-white/5 text-left">
              <tr>
                <th className="px-3 py-2">주문번호</th>
                <th className="px-3 py-2">구매자</th>
                <th className="px-3 py-2">판매자수</th>
                <th className="px-3 py-2">금액</th>
                <th className="px-3 py-2">상태</th>
                <th className="px-3 py-2">일시</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.order_id} className="border-t border-black/5 dark:border-white/10" data-testid={`admin-order-row-${o.order_id}`}>
                  <td className="px-3 py-2 font-bold">{o.order_number}</td>
                  <td className="px-3 py-2">{o.buyer_snapshot?.name || '-'}<br /><span className="text-[11px] text-gray-500">{o.buyer_snapshot?.email}</span></td>
                  <td className="px-3 py-2">{o.totals?.seller_count || (o.seller_orders || []).length}</td>
                  <td className="px-3 py-2 font-bold">{won(o.totals?.total)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_STYLE[o.status] || 'bg-gray-100'}`}>{o.status_label}</span>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-gray-500">{o.created_at && format(new Date(o.created_at), 'yyyy.MM.dd HH:mm', { locale: ko })}</td>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => openDetail(o.order_id)}
                      className="px-3 py-1 rounded-full text-xs font-bold bg-black text-white"
                      data-testid={`admin-order-open-${o.order_id}`}>상세</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-6" onClick={closeDetail} data-testid="admin-order-detail-modal">
          <div className="bg-white dark:bg-[#141414] w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
            {!detail ? (
              <p className="text-sm text-gray-400 py-6">불러오는 중…</p>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-black">주문 상세 · {detail.order_number}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{detail.buyer_snapshot?.name} · {detail.buyer_snapshot?.email}</p>
                  </div>
                  <button type="button" onClick={closeDetail} className="text-xs">닫기</button>
                </div>

                <div className="mb-3 flex flex-wrap gap-2">
                  {['payment_pending', 'paid', 'preparing', 'shipping', 'delivered', 'confirmed'].map((st) => (
                    <button key={st} type="button" disabled={busy || detail.status === st}
                      onClick={() => changeStatus(st)}
                      className={`px-3 py-1.5 text-xs font-bold rounded-full border transition-colors ${detail.status === st ? 'bg-[#FF3B30] border-[#FF3B30] text-white' : 'border-black/10 dark:border-white/10 hover:border-[#FF3B30]'}`}
                      data-testid={`admin-detail-status-${st}`}>
                      {st}
                    </button>
                  ))}
                  <button type="button" onClick={cancelOrder} disabled={busy}
                    className="px-3 py-1.5 text-xs font-bold rounded-full bg-gray-700 text-white"
                    data-testid="admin-detail-cancel">취소 승인</button>
                  <button type="button" onClick={refundOrder} disabled={busy}
                    className="px-3 py-1.5 text-xs font-bold rounded-full bg-orange-600 text-white"
                    data-testid="admin-detail-refund">환불 승인</button>
                </div>

                <div className="mb-3 p-3 rounded-lg bg-black/[0.03] dark:bg-white/[0.03] text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-bold">배송지</p>
                    {!editAddr && (
                      <button type="button" onClick={startEditAddr} className="text-[11px] font-bold text-[#FF3B30]" data-testid="admin-detail-edit-shipping">배송정보 수정</button>
                    )}
                  </div>
                  {editAddr ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1" data-testid="admin-detail-edit-form">
                      <input placeholder="받는 사람" className="px-2 py-1.5 border border-black/10 dark:border-white/10 rounded text-xs bg-transparent" value={addrForm.recipient_name || ''} onChange={(e) => setAddrForm({ ...addrForm, recipient_name: e.target.value })} data-testid="admin-edit-name" />
                      <input placeholder="휴대전화" className="px-2 py-1.5 border border-black/10 dark:border-white/10 rounded text-xs bg-transparent" value={addrForm.phone || ''} onChange={(e) => setAddrForm({ ...addrForm, phone: e.target.value })} data-testid="admin-edit-phone" />
                      <div className="flex gap-2">
                        <input placeholder="우편번호" className="flex-1 px-2 py-1.5 border border-black/10 dark:border-white/10 rounded text-xs bg-transparent" readOnly value={addrForm.postal_code || ''} data-testid="admin-edit-postal" />
                        <AddressSearch onComplete={applySearchResult} detailInputRef={detailInputRef} testId="admin-edit-search" />
                      </div>
                      <input placeholder="기본 주소" className="px-2 py-1.5 border border-black/10 dark:border-white/10 rounded text-xs bg-transparent" readOnly value={addrForm.address1 || ''} data-testid="admin-edit-line1" />
                      <input ref={detailInputRef} placeholder="상세 주소" className="px-2 py-1.5 border border-black/10 dark:border-white/10 rounded text-xs bg-transparent sm:col-span-2" value={addrForm.address2 || ''} onChange={(e) => setAddrForm({ ...addrForm, address2: e.target.value, detail_address: e.target.value })} data-testid="admin-edit-line2" />
                      <input placeholder="배송 요청사항" className="px-2 py-1.5 border border-black/10 dark:border-white/10 rounded text-xs bg-transparent sm:col-span-2" value={addrForm.buyer_message || ''} onChange={(e) => setAddrForm({ ...addrForm, buyer_message: e.target.value })} data-testid="admin-edit-message" />
                      <input placeholder="변경 사유" className="px-2 py-1.5 border border-black/10 dark:border-white/10 rounded text-xs bg-transparent sm:col-span-2" value={addrForm.reason || ''} onChange={(e) => setAddrForm({ ...addrForm, reason: e.target.value })} data-testid="admin-edit-reason" />
                      <div className="flex gap-2 sm:col-span-2">
                        <button onClick={saveEditAddr} disabled={busy} className="flex-1 py-1.5 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40" data-testid="admin-edit-save">저장</button>
                        <button onClick={() => setEditAddr(false)} className="flex-1 py-1.5 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold">취소</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p>{detail.shipping_address_snapshot?.recipient_name} · {detail.shipping_address_snapshot?.phone}</p>
                      <p className="text-gray-500">[{detail.shipping_address_snapshot?.postal_code}] {detail.shipping_address_snapshot?.address1} {detail.shipping_address_snapshot?.address2}</p>
                      {detail.buyer_message && <p className="text-gray-500 mt-0.5">요청사항: {detail.buyer_message}</p>}
                    </>
                  )}
                </div>

                <div className="space-y-3">
                  {(detail.seller_orders || []).map((so) => (
                    <div key={so.seller_order_id} className="rounded-xl border border-black/10 dark:border-white/10 p-3">
                      <div className="flex items-center justify-between mb-2 text-xs">
                        <span className="font-bold">{so.seller_name_snapshot}</span>
                        <span className={`inline-flex px-2 py-0.5 rounded-full font-bold ${STATUS_STYLE[so.status] || 'bg-gray-100'}`}>{so.status_label}</span>
                      </div>
                      <ul className="text-xs space-y-1">
                        {(so.items || []).map((it, idx) => (
                          <li key={idx} className="flex items-center justify-between">
                            <span className="truncate">{it.name_snapshot} × {it.quantity}</span>
                            <span className="font-bold">{won(it.line_total)}</span>
                          </li>
                        ))}
                      </ul>
                      {so.shipment?.tracking_number && (
                        <p className="mt-2 text-[11px] text-gray-500">배송: {so.shipment.courier} / {so.shipment.tracking_number}</p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex items-center justify-between border-t border-black/5 dark:border-white/10 pt-3">
                  <span className="text-sm text-gray-500">총 결제 예정</span>
                  <span className="text-lg font-black text-[#FF3B30]">{won(detail.totals?.total)}</span>
                </div>

                <div className="mt-4 rounded-xl border border-black/10 dark:border-white/10 p-3">
                  <p className="text-xs font-black mb-2 inline-flex items-center gap-1"><ClipboardList size={12} /> 전체 변경 이력</p>
                  {history.length === 0 ? (
                    <p className="text-[11px] text-gray-500">기록 없음</p>
                  ) : (
                    <ul className="text-[11px] space-y-1" data-testid="admin-detail-history">
                      {history.map((h) => (
                        <li key={h.history_id} className="flex justify-between gap-2">
                          <span>
                            <span className="font-bold">{ACTION_LABELS[h.action] || h.action}</span>
                            {(h.action === 'status.update' || h.action === 'status.rollback') && (
                              <> · <span className="text-gray-500">{h.before?.status}</span> → <span className="font-bold">{h.after?.status}</span></>
                            )}
                            {h.action === 'invoice.set' && h.after?.shipment && (
                              <> · <span className="font-bold">{h.after.shipment.courier}</span> / <span className="font-mono">{h.after.shipment.tracking_number}</span></>
                            )}
                            {h.action === 'address.update' && h.after?.shipping_address_snapshot && (
                              <> · {h.after.shipping_address_snapshot.recipient_name} · {h.after.shipping_address_snapshot.phone}</>
                            )}
                            <span className="text-gray-500"> · {h.actor_role}{h.actor_name ? ` (${h.actor_name})` : ''}</span>
                            {h.reason && <span className="text-gray-400"> · {h.reason}</span>}
                          </span>
                          <span className="text-gray-400 shrink-0">{format(new Date(h.created_at), 'MM.dd HH:mm', { locale: ko })}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrders;
